import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';

import { ClassNames, LhChangeItem, LhClickBrandLogo, LhMobileSearch, LhSticky } from '../../utils/interfaces';

const logo = (
  <svg version="1.1" id="Brand" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 1000 124">
    <path
      d="M238.9,95.2h31.4V124h-89.9V0h89v28.8h-30.6v19.8h27.9v26.1h-27.9V95.2z M101.3,124h59.3V0h-59.3V124z M58.4,0H0l0,124h85.4
            V90.8h-27V0z M654.1,74.6H682V48.5h-27.9V28.8h30.6V0h-89v124h89.9V95.2h-31.5V74.6z M423.4,90.7c0,16.2-9.5,26.8-25.7,30.7
            c-7.1,1.7-18.6,2.6-29,2.6h-80.5V0h80c10.2,0,21.4,0.9,28.4,2.6c15.8,3.9,25.1,14.5,25.1,30.7c0,14.9-8.8,25.1-20,28.4
            C413.7,64.4,423.4,75,423.4,90.7 M344.7,50.3h8.1c10.8,0,14.4-5.4,14.4-13s-3.6-13-14.4-13h-8.1V50.3z M369,86.3
            c0-8.1-3.7-13.5-15.3-13.5h-9v27h9C365.4,99.7,369,94.3,369,86.3 M517,45.8h-19.8V0h-58.4v124h58.4V76.4H517V124h58.4V0H517V45.8z
            M967.9,72.3c15.3-7,22.1-18.6,22.1-34.4c0-18.4-11-30.4-29.4-34.9c-8.1-1.9-21.2-3-33-3h-71.9v124h55.7V84.5h5l21.1,39.5h62.5
            L967.9,72.3z M919.5,58.4h-8.1V28.8h8.1c10.8,0,14.4,5.4,14.4,14.8C933.9,53,930.3,58.4,919.5,58.4z M815.4,72.3
            c15.3-7,22.1-18.6,22.1-34.4c0-18.4-11-30.4-29.4-34.9c-8.1-1.9-21.2-3-33-3h-71.9v124h55.7V84.5h5l21.1,39.5h62.5L815.4,72.3z
            M766.9,58.4h-8.1V28.8h8.1c10.8,0,14.4,5.4,14.4,14.8C781.3,53,777.7,58.4,766.9,58.4z"
    />
  </svg>
);

export interface SubitemsValue {
  label: string;
  items: [{ label: string; itemId: string }];
  itemId: string;
}

@Component({
  tag: 'patternlib-header-portal',
  styleUrl: 'patternlib-header-portal.scss',
  shadow: true,
})
export class PatternlibHeaderPortal {
  private hasPartnerLogoSlot: boolean;
  private hasPortalName: boolean;
  private hasPortalNameOrPartnerLogo: boolean;
  private offsetTop: number;
  private tabElement: HTMLPatternlibTabsElement;
  private scrollPosition: number;
  private isStickyAndMenuisOpen = false;
  private hasMoreSlot: boolean;
  private hasSearchSlot: boolean;
  private initialRender = true;
  private lastOpendTab: number | string;

  @Element() hostElement: HTMLPatternlibHeaderPortalElement;

  /**
   * Set the padding for the header
   */
  @Prop() padding?: string;

  /**
   * Define the portal name
   */
  @Prop() portalName?: string = '';

  /**
   * Set the header to be sticky if the pageYOffset is greater than the header's offsetTop
   */
  @Prop() sticky?: boolean;

  /**
   * Set the header to be sticky by default
   */
  @Prop() stickyByDefault?: boolean;

  /**
   * Provide a path to the brand logo image file
   */
  @Prop() brandLogo: string;

  /**
   * Define the width of the more slot displayed in the mobile menu
   */
  @Prop() moreSlotWidth = 'auto';

  /**
   * Set state if mobile menu is opened or closed
   */
  @Prop({ mutable: true }) mobileMenuIsOpen = false;

  /**
   * If true the default padding values get applied.
   */
  @Prop() usePadding = false;

  /**
   * Internal state if header is sticky
   */
  @State() isSticky = false;

  /**
   * Internal state to save state if tab-item menu is opened or closed
   */
  @State() tabItemMenuIsOpen = false;

  /**
   * Internal state to save the TabItems
   */
  @State() tabItems: HTMLPatternlibTabsItemElement[] = [];

  /**
   * Internal state to save the tab-item's index
   */
  @State() tabItemIndex: number;

  /**
   * Internal state to save the tab-subitem's index
   */
  @State() subItemIndex: number;

  /**
   * Total number of unseen notifications. Is set by the notification-flyout-service
   */
  @State() unseenNotificationCount = 0;

  /*
    * watch 'portalName' and set it if it has changed or has been
      provided later (e.g. when translation is loaded)
  */
  @Watch('portalName')
  updateHasPortalName(): void {
    this.hasPortalName = this.portalName !== '' && this.portalName !== null;
    this.hasPortalNameOrPartnerLogo = this.hasPortalName || this.hasPartnerLogoSlot;
  }

  @Listen('lhUnseenNotificationCountChange')
  updateUnseenNotificationCount(evt) {
    if (evt.detail !== '') {
      this.unseenNotificationCount = evt.detail;
    }
  }

  async componentWillLoad(): Promise<void> {
    this.hasPartnerLogoSlot = this.hostElement.querySelector('[slot="partner-logo"]') !== null;
    this.tabElement = this.hostElement.querySelector('patternlib-tabs');
    this.hasPortalName = this.portalName !== '';
    this.hasPortalNameOrPartnerLogo = this.hasPortalName || this.hasPartnerLogoSlot;
    this.offsetTop = this.hostElement.offsetTop;
    this.hasMoreSlot = this.hostElement.querySelector('[slot="more"]') !== null;
    this.hasSearchSlot = this.hostElement.querySelector('[slot="search"]') !== null;

    if (this.tabElement != null) {
      this.tabElement.setAttribute('padding-top', '9px');
    }

    this.tabItems = Array.from(this.hostElement.querySelectorAll('patternlib-tabs-item'));
  }

  // Fix Firefox bug where initial offsetTop returns a negative value and force to set tab to active to check scroll overflow again.
  async componentDidLoad(): Promise<void> {
    this.offsetTop = this.hostElement.offsetTop;

    let i = 0;
    for (const tab of this.tabItems) {
      if (tab.active) {
        await this.tabElement.setTabToActive(i);
        this.lastOpendTab = i;
      }
      i++;
    }
  }

  componentDidUpdate(): void {
    this.offsetTop = this.hostElement.offsetTop;
  }

  /**
   * Provide a method to be triggered if the pageYOffset is greater than the header's offsetTop.
   */
  @Event({ eventName: 'lhSticky' }) lhSticky: EventEmitter<LhSticky>;

  /**
   * Provide a method to be triggered each time a tab is clicked.
   */
  @Event({ eventName: 'lhTabClick' }) tabClick: EventEmitter<LhChangeItem>;
  /**
   * Provide a method to get the searchbox value when the search icon get clicked.
   */
  @Event({ eventName: 'lhMobileSearch' }) mobileSearch: EventEmitter<LhMobileSearch>;

  /**
   * Provide a method to get the searchbox value when the Enter key get pressed.
   */
  @Event({ eventName: 'lhMobileEnterPress' }) mobileEnterPress: EventEmitter<LhMobileSearch>;

  /**
   * Provide a method to be triggered each time the brand-logo is clicked.
   */
  @Event({ eventName: 'lhClickBrandLogo' }) clickBrandLogo: EventEmitter<LhClickBrandLogo>;

  @Listen('scroll', { target: 'window' })
  windowScroll(): void {
    const isOpen = this.mobileMenuIsOpen || this.tabItemMenuIsOpen;

    if (this.sticky) {
      if (window.pageYOffset > this.offsetTop && isOpen === false) {
        this.isSticky = true;
        this.lhSticky.emit({ isSticky: this.isSticky });
      } else if (window.pageYOffset <= this.offsetTop && isOpen === false) {
        this.isSticky = false;
        this.lhSticky.emit({ isSticky: this.isSticky });
      }
    }

    if (isOpen === false) {
      this.scrollPosition = window.pageYOffset;
    }
  }

  @Listen('lhChangeItem')
  handleTabChange(e): void {
    const itemId = e.detail.itemId;
    if (this.initialRender == false) {
      this.tabItems.map((tab, i) => {
        if (i === itemId || tab.itemId === itemId) {
          const index = i;
          if (this.mobileMenuIsOpen === false) {
            if (this.tabItems[index].subitems != undefined) {
              if (this.tabItemMenuIsOpen === false && this.tabItemIndex != index) {
                this.tabItemIndex = index;
                this.tabItemMenuIsOpen = true;
                if (this.sticky) {
                  this.isSticky = true;
                  this.isStickyAndMenuisOpen = true;
                  this.lhSticky.emit({ isSticky: this.isSticky });
                }
                this.fixBodyPosition();
              } else if (this.tabItemMenuIsOpen && this.tabItemIndex != index) {
                this.tabItemIndex = index;
              } else if (this.tabItemMenuIsOpen && this.tabItemIndex === index) {
                this.tabItemMenuIsOpen = false;
                this.tabItemIndex = undefined;
                this.fixBodyPosition();
              }
            } else {
              this.tabItemMenuIsOpen = false;
              this.tabItemIndex = undefined;
              this.fixBodyPosition();
              if (this.lastOpendTab != itemId) {
                this.tabClick.emit({ itemId: itemId });
                this.lastOpendTab = itemId;
              }
            }
          } else {
            this.mobileMenuIsOpen = false;
            this.fixBodyPosition();
          }
        }
      });
    }
    this.initialRender = false;
  }

  @Listen('keydown', { target: 'document' })
  async handleEscapePress(e: KeyboardEvent): Promise<void> {
    if (e.key === 'Escape') {
      if (this.mobileMenuIsOpen && this.tabItemMenuIsOpen === false) {
        this.handleMenuClick();
      } else if (this.mobileMenuIsOpen === false && this.tabItemMenuIsOpen) {
        await this.handleCloseTabItemMenu();
      }
    }
  }

  @Listen('lhEnterPress')
  handleEnterPress(e): void {
    const value = e.detail;
    this.mobileEnterPress.emit({ value: value });
  }

  /**
   * @deprecated Set the prop screenSize from outside (e.g. ResizeObserver)
   *
   * Will be removed with 3.0. Does nothing.
   *
   * @param {ScreenSize} _screen - The size of the Screen
   * @param _height
   */
  @Method()
  async setScreenSize(_screen: any, _height: number): Promise<void> {
    // this.screenSize = screen;
    // this.screenHeight = height;
  }

  private fixBodyPosition(): void {
    const isSticky = this.isSticky || this.stickyByDefault;
    // Fix the body's position if the mobile menu is open and remove the property if mobile menu get closed
    if (this.isStickyAndMenuisOpen === false) {
      if ((this.mobileMenuIsOpen && isSticky) || (this.tabItemMenuIsOpen && isSticky)) {
        document.body.style.position = 'fixed';
      } else {
        document.body.style.position = '';
        // Set the scroll position back to last know position
        window.scrollTo(0, this.scrollPosition);
      }
    } else if (this.isStickyAndMenuisOpen) {
      if ((this.mobileMenuIsOpen && isSticky) || (this.tabItemMenuIsOpen && isSticky)) {
        document.body.style.position = 'fixed';
      } else {
        document.body.style.position = '';
        this.isStickyAndMenuisOpen = false;
        // Set the scroll position back to last know position
        if (this.scrollPosition <= this.offsetTop || this.scrollPosition === undefined) {
          this.isSticky = false;
          this.lhSticky.emit({ isSticky: this.isSticky });
          // Try to scroll to offsetTop
          window.scrollTo(0, this.offsetTop);
        } else {
          window.scrollTo(0, this.scrollPosition);
        }
      }
    }
  }

  private async closeMenus(): Promise<void> {
    if (this.tabItemIndex != this.lastOpendTab) {
      await this.tabElement.setTabToActive(this.lastOpendTab);
    }
    this.tabItemMenuIsOpen = false;
    this.mobileMenuIsOpen = false;
    this.tabItemIndex = undefined;
  }

  private handleMenuClick = (): void => {
    this.mobileMenuIsOpen = !this.mobileMenuIsOpen;
    if (this.sticky) {
      this.isSticky = true;
      this.isStickyAndMenuisOpen = true;
      this.lhSticky.emit({ isSticky: this.isSticky });
    }
    this.fixBodyPosition();
  };

  private handleTabClick = (itemId: number | string, index2?: string | number, index3?: string | number) => async () => {
    if (itemId != undefined && index2 === undefined && index3 === undefined) {
      this.tabClick.emit({ itemId: itemId });
      this.lastOpendTab = itemId;
      await this.tabElement.setTabToActive(itemId);
      this.tabItemMenuIsOpen = false;
      this.mobileMenuIsOpen = false;
      this.tabItemIndex = undefined;
      this.fixBodyPosition();
    } else if (itemId != undefined && index2 != undefined && index3 === undefined) {
      this.tabClick.emit({ itemId: itemId, subitemId: index2 });
      this.lastOpendTab = itemId;
      this.tabItemMenuIsOpen = false;
      this.mobileMenuIsOpen = false;
      this.tabItemIndex = undefined;
      this.fixBodyPosition();
    } else if (itemId != undefined && index2 != undefined && index3 != undefined) {
      this.tabClick.emit({ itemId: itemId, subitemId: index2, subSubitemId: index3 });
      this.lastOpendTab = itemId;
      this.tabItemMenuIsOpen = false;
      this.mobileMenuIsOpen = false;
      this.tabItemIndex = undefined;
      this.fixBodyPosition();
    }
  };

  private handleCloseTabItemMenu = async (): Promise<void> => {
    await this.closeMenus();
    this.fixBodyPosition();
  };

  private handleToggleSubitems = (index: number, index2?: number) => (): void => {
    if (index != undefined && index2 === undefined) {
      this.tabItems = this.tabItems.map((tab, i) => {
        if (i === index) {
          tab.subitemsAreCollapsed = !tab.subitemsAreCollapsed;
        } else {
          tab.subitemsAreCollapsed = true;
        }
        return tab;
      });
    } else if (index != undefined && index2 != undefined) {
      if (this.subItemIndex === index + index2) {
        this.subItemIndex = undefined;
      } else {
        this.subItemIndex = index + index2;
      }
    }
  };

  private handleBrandLogoClick = (): void => {
    this.clickBrandLogo.emit();
  };

  render(): any {
    const mobileMenuTabitemWrapperClassName = 'mobile-menu-tabitem-wrapper';

    const headerClass: ClassNames = {
      'header-container': true,
      'sticky': this.isSticky || this.stickyByDefault,
      'px-[20px] desktop:px-[120px]': this.usePadding,
    };

    const headerWrapperClasses = {
      'header-wrapper grid items-center h-full': true,
      'portal': this.hasPortalNameOrPartnerLogo,
    };

    const portalNameClass: ClassNames = {
      'portal-name': true,
    };

    const partnerLogoDeviderClass: ClassNames = {
      'partner-logo-devider': true,
    };

    const menuButtonClass: ClassNames = {
      'menu-button': true,
      'mobile-menu-open': this.mobileMenuIsOpen,
    };

    const mobileMenuClass: ClassNames = {
      'mobile-menu': true,
    };

    const mobileMenuTabitemWrapperClass: ClassNames = {
      [mobileMenuTabitemWrapperClassName]: true,
    };

    const mobileMenuTabitemClass: ClassNames = {
      'mobile-menu-tabitem': true,
      'overflow-hidden': true,
    };

    const mobileMenuExpandSubitemsIconClass: ClassNames = {
      'mobile-menu-expand-subitems-icon': true,
    };

    const mobileMenuTabitemWrapperSubitemsClass: ClassNames = {
      [mobileMenuTabitemWrapperClassName]: true,
      subitem: true,
    };

    const mobileMenuSubSubitemClass: ClassNames = {
      'mobile-menu-subitem': true,
    };

    const mobileMenuTabitemWrapperSubSubitemsClass: ClassNames = {
      [mobileMenuTabitemWrapperClassName]: true,
      'mobile-menu-sub-subitem': true,
    };

    const tabitemMenuClass: ClassNames = {
      'tabitem-menu': true,
    };

    const tabitemMenuTabitemWrapperClass: ClassNames = {
      'tabitem-menu-tabitem-wrapper': true,
    };

    const tabitemMenuTabitemUnderlineClass: ClassNames = {
      'tabitem-menu-tabitem-underline': true,
    };

    const tabitemMenuSubitemContainerClass: ClassNames = {
      'tabitem-menu-subitem-container': true,
    };

    const tabitemMenuSubSubitemContainerClass: ClassNames = {
      'tabitem-menu-sub-subitem-container': true,
    };

    const tabitemMenuSubSubitemWrapperClass: ClassNames = {
      'tabitem-menu-sub-subitem-wrapper': true,
    };

    const headerContainerStyles = {
      boxShadow: this.isSticky || this.stickyByDefault ? 'var(--shadow-elevation-basic)' : undefined,
      padding: !this.usePadding ? this.padding : undefined,
    };

    const partnerLogoDeviderStyles = { marginLeft: this.hasPortalName ? 'var(--spacing-m)' : '0px' };

    const mobileMenuWrapperStyles = { gridTemplateRows: this.hasSearchSlot ? '80px 1fr 80px' : '1fr 80px' };

    const moreSlotStyles = { width: this.moreSlotWidth };

    const menuIcon = (
      <button class={menuButtonClass} onClick={this.handleMenuClick}>
        {this.mobileMenuIsOpen ? (
          // "close" icon (rotation of lines with CSS transform)
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="15" y="23" width="50" height="2" fill="black" class="bar1" />
            <rect x="15" y="40" width="50" height="2" fill="black" class="bar2" />
          </svg>
        ) : (
          // hamburger icon
          <div class="burger-wrapper">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="15" y="23" width="34" height="2" fill="black" class="bar1" />
              <rect x="15" y="40" width="34" height="2" fill="black" class="bar2" />
            </svg>
            {this.unseenNotificationCount > 0 ? (
              <patternlib-badge icon-name badge-size="responsive" value={this.unseenNotificationCount < 100 ? this.unseenNotificationCount.toString() : '99+'}></patternlib-badge>
            ) : null}
          </div>
        )}
      </button>
    );

    const closeIcon = (
      <button class={menuButtonClass} onClick={this.handleCloseTabItemMenu}>
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.5 48.5L48.5 15.5" stroke="black" stroke-width="2" />
          <path d="M48.5 48.5L15.5 15.5" stroke="black" stroke-width="2" />
        </svg>
      </button>
    );

    const mobileMenu = (
      <div role="navigation" class={mobileMenuClass}>
        <div class="grid h-full" style={mobileMenuWrapperStyles}>
          {this.hasSearchSlot && (
            <div class="flex items-center h-full px-[24px] bg-white">
              <div class="w-full">
                <patternlib-searchbox type="mobile"></patternlib-searchbox>
              </div>
            </div>
          )}

          <div class="overflow-auto">
            <ul>
              {this.tabItems
                .filter(x => x.show)
                .map((tabItem: HTMLPatternlibTabsItemElement, index: number) => {
                  let _subitems: SubitemsValue[];

                  if (tabItem.subitems != undefined) {
                    _subitems = JSON.parse(tabItem.subitems);
                  }

                  const itemId = tabItem.itemId != undefined ? tabItem.itemId : index;

                  const collapseSubitemsIconStyles = {
                    transform: tabItem.subitemsAreCollapsed === false ? 'rotate(180deg)' : 'rotate(360deg)',
                  };

                  return (
                    <li>
                      <div class={mobileMenuTabitemWrapperClass}>
                        <a>
                          <div class="grid px-[24px]" style={{ gridTemplateColumns: _subitems != undefined ? '1fr 64px' : '1fr' }}>
                            <div class={mobileMenuTabitemClass} onClick={this.handleTabClick(itemId)}>
                              <span>{tabItem.label}</span>
                            </div>

                            {_subitems != undefined && (
                              <div class={mobileMenuExpandSubitemsIconClass} style={collapseSubitemsIconStyles} onClick={this.handleToggleSubitems(index)}>
                                <patternlib-icon icon-name="arrow-down" size="44px" />
                              </div>
                            )}
                          </div>
                        </a>
                      </div>
                      {_subitems != undefined && tabItem.subitemsAreCollapsed === false && (
                        <ul>
                          {_subitems.map((item, index2: number) => {
                            const collapseSubSubitemsIconStyles = {
                              transform: this.subItemIndex === index + index2 ? 'rotate(180deg)' : 'rotate(360deg)',
                            };
                            const subitemId = item.itemId || index2;

                            return (
                              <li>
                                <div class={mobileMenuTabitemWrapperSubitemsClass}>
                                  <a>
                                    <div class="grid px-[24px]" style={{ gridTemplateColumns: _subitems[index2].items != undefined ? '1fr 64px' : '1fr' }}>
                                      <div class={mobileMenuSubSubitemClass} onClick={this.handleTabClick(itemId, subitemId)}>
                                        <span>{item.label}</span>
                                      </div>

                                      {_subitems[index2].items != undefined && (
                                        <div class={mobileMenuExpandSubitemsIconClass} style={collapseSubSubitemsIconStyles} onClick={this.handleToggleSubitems(index, index2)}>
                                          <patternlib-icon icon-name="arrow-down" size="44px" />
                                        </div>
                                      )}
                                    </div>
                                  </a>
                                </div>
                                {_subitems[index2].items != undefined && this.subItemIndex === index + index2 && (
                                  <ul>
                                    {_subitems[index2].items.map((item, index3: number) => {
                                      const subSubitemId = item.itemId || index3;
                                      return (
                                        <li>
                                          <a>
                                            <div class={mobileMenuTabitemWrapperSubSubitemsClass} onClick={this.handleTabClick(itemId, subitemId, subSubitemId)}>
                                              <span>{item.label}</span>
                                            </div>
                                          </a>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              <slot />
            </ul>
          </div>
          <div class="bg-white px-[24px]">
            <div class="flex justify-end items-center bg-white h-full">
              <div style={moreSlotStyles}>
                <slot name="more" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    const tabItemMenu = (
      <div role="navigation" class={tabitemMenuClass}>
        <div class="flex justify-end p-[24px]">{closeIcon}</div>
        <div class="h-full pb-[96px] ml-[224px]">
          <div class="text-left">
            <ul>
              {this.tabItems.map((tabItem: HTMLPatternlibTabsItemElement, index: number) => {
                if (index === this.tabItemIndex) {
                  const _subitems = JSON.parse(tabItem.subitems);
                  const itemId = tabItem.itemId != undefined ? tabItem.itemId : index;

                  return (
                    <li>
                      <div class={tabitemMenuTabitemWrapperClass} onClick={this.handleTabClick(itemId)}>
                        <a>
                          <span class={tabitemMenuTabitemUnderlineClass}>{tabItem.label}</span>
                        </a>
                      </div>
                      <ul>
                        {_subitems.map((item, index2: number) => {
                          const subitemId = item.itemId || index2;
                          return (
                            <li>
                              <div class={tabitemMenuSubitemContainerClass}>
                                <div class={tabitemMenuTabitemWrapperClass} onClick={this.handleTabClick(itemId, subitemId)}>
                                  <a>
                                    <span class={tabitemMenuTabitemUnderlineClass}>{item.label}</span>
                                  </a>
                                </div>
                                {_subitems[index2].items != undefined && (
                                  <div class={tabitemMenuSubSubitemContainerClass}>
                                    <ul>
                                      {_subitems[index2].items.map((item, index3: number) => {
                                        const subSubitemId = item.itemId || index3;
                                        return (
                                          <li>
                                            <div class={tabitemMenuSubSubitemWrapperClass} onClick={this.handleTabClick(itemId, subitemId, subSubitemId)}>
                                              <a>{item.label}</a>
                                            </div>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  );
                }
              })}
            </ul>
          </div>
        </div>
      </div>
    );

    const additionals =
      this.hasMoreSlot && this.hasSearchSlot ? (
        <div class="grid grid-flow-col gap-[48px]">
          <slot name="search" />
          <slot name="more" />
        </div>
      ) : this.hasMoreSlot ? (
        <slot name="more" />
      ) : this.hasSearchSlot ? (
        <slot name="search" />
      ) : null;

    return (
      <Host>
        <header class={headerClass} style={headerContainerStyles}>
          <div class={headerWrapperClasses}>
            <div class="cursor-pointer w-[124px] lg:w-[165px]">
              {!this.brandLogo ? (
                <div onClick={this.handleBrandLogoClick}>{logo}</div>
              ) : (
                <patternlib-image src={this.brandLogo} alt="Brand Logo" onClick={this.handleBrandLogoClick}></patternlib-image>
              )}
            </div>

            {this.hasPortalNameOrPartnerLogo && (
              <div class="hidden sm:flex items-center w-full h-full">
                {this.hasPortalName && <span class={portalNameClass}>{this.portalName}</span>}
                {this.hasPartnerLogoSlot && <div class={partnerLogoDeviderClass} style={partnerLogoDeviderStyles}></div>}
                {this.hasPartnerLogoSlot && <slot name="partner-logo" />}
              </div>
            )}

            <div class="hidden lg:block overflow-auto">
              <slot name="tabs" />
            </div>

            {!this.mobileMenuIsOpen && <div class="hidden lg:block">{additionals}</div>}

            <div
              class={{
                'flex items-center justify-end w-full h-full': true,
                'lg:hidden': !this.mobileMenuIsOpen,
              }}
            >
              {menuIcon}
            </div>
          </div>
          <div class="hidden lg:block">{!this.mobileMenuIsOpen && <slot />}</div>
          {this.mobileMenuIsOpen && mobileMenu}
          {this.tabItemMenuIsOpen && tabItemMenu}
        </header>
      </Host>
    );
  }
}
